var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// ****************
// App Entry Point.
// ****************
import $ from "jQuery";
import App from "./app.vue";
import { createApp, defineAsyncComponent } from 'vue';
import { createBootstrap } from 'bootstrap-vue-next';
import store from "@/app/store/store";
import { router } from "@/app/router/router";
import ErrorHandler from "@/common/utils/error-handler";
import Notifications from '@kyvg/vue3-notification';
import { configure } from 'vee-validate';
import { addMethod, string, object } from 'yup';
import VueMultiselect from "vue-multiselect";
import AlertComponent from "@/app/vues/components/alert.vue";
import ConfirmComponent from "@/app/vues/components/confirm.vue";
import CollapsibleFormVue from "@/app/vues/components/collapsible-form.vue";
import FloatingActionMenuComponent from "@/app/vues/components/floating-action-menu.vue";
import SortableColumnComponent from "@/app/vues/components/sortable-column.vue";
import StatusOverlayComponent from "@/app/vues/components/status-overlay.vue";
// vue-flatpickr-component is loaded on demand only when a page renders <flat-pickr>.
const flatPickr = defineAsyncComponent(() => import(/* webpackChunkName: "flatpickr" */ 'vue-flatpickr-component'));
import { vLicense, vCloudOnly, vOnPremOnly, vStandardOrHigher, vPremiumOnly } from "@/app/directives/license.directive";
window.$ = $;
(() => __awaiter(void 0, void 0, void 0, function* () {
    const isLoggedIn = store.getters["loginModule/isLoggedIn"];
    if (isLoggedIn) {
        yield store.dispatch("licenseModule/loadLicense");
    }
    const app = createApp(App);
    app.config.performance = true;
    app.use(store);
    app.use(router);
    app.use(createBootstrap({
        components: {
            all: false,
            BAlert: true,
            BButton: true,
            BButtonGroup: true,
            BCard: true,
            BCardBody: true,
            BCardHeader: true,
            BCol: true,
            BContainer: true,
            BDropdown: true,
            BDropdownDivider: true,
            BDropdownForm: true,
            BDropdownItem: true,
            BForm: true,
            BFormCheckbox: true,
            BFormCheckboxGroup: true,
            BFormFile: true,
            BFormGroup: true,
            BFormInput: true,
            BFormRadio: true,
            BFormRadioGroup: true,
            BFormSelect: true,
            BFormTextarea: true,
            BInputGroup: true,
            BInputGroupAppend: true,
            BInputGroupText: true,
            BModal: true,
            BPagination: true,
            BPopover: true,
            BProgress: true,
            BProgressBar: true,
            BRow: true,
            BTab: true,
            BTabs: true,
        },
        directives: {
            all: false,
            vBTooltip: true,
        },
    }));
    app.use(Notifications);
    // Register license directives globally
    app.directive("license", vLicense);
    app.directive("cloud-only", vCloudOnly);
    app.directive("onprem-only", vOnPremOnly);
    app.directive("standard-or-higher", vStandardOrHigher);
    app.directive("premium-only", vPremiumOnly);
    app.component("multiselect", VueMultiselect);
    app.component("AlertComponent", AlertComponent);
    app.component("Confirm", ConfirmComponent);
    app.component("CollapsibleForm", CollapsibleFormVue);
    app.component("FloatingActionMenuComponent", FloatingActionMenuComponent);
    app.component("SortableColumnComponent", SortableColumnComponent);
    app.component("StatusOverlayComponent", StatusOverlayComponent);
    app.component("flatPickr", flatPickr);
    // Custom Yup validation methods
    addMethod(string, 'phoneNumber', function phoneNumber() {
        return this.test('phoneNumber', 'Invalid Phone Number', (value) => {
            let regExp = new RegExp("^(?=.*[0-9])[- +()0-9]+$");
            return !value || (regExp.test(value) && value.length >= 7);
        });
    });
    addMethod(string, 'customPassword', function customPassword() {
        return this.test('customPassword', 'The password must be a mix of letters and numbers, as well as upper and lower-case characters.', (value) => {
            if (!value || value.length < 12)
                return false;
            let regExp = new RegExp("(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])");
            let notMixOfLettersAndNumbers = new RegExp("(?=.*^[a-z]*[A-Z]*[0-9]*$)");
            return regExp.test(value) && !notMixOfLettersAndNumbers.test(value);
        });
    });
    addMethod(object, 'listOptionRequired', function listOptionRequired() {
        return this.test('listOptionRequired', 'Select an option', (value) => {
            if (!value)
                return false;
            return value.value;
        });
    });
    configure({
        validateOnInput: true,
    });
    //// Global error handler.
    const isProduction = process.env.NODE_ENV === "production";
    const isCloudHosting = store.getters["licenseModule/isCloud"];
    const useSentry = isProduction && isCloudHosting;
    if (useSentry) {
        // Lazy-load Sentry only when it will actually be used (production + cloud hosting).
        import(/* webpackChunkName: "sentry" */ "@sentry/vue")
            .then((Sentry) => {
            Sentry.init({
                app,
                environment: window.location.hostname,
                dsn: "https://d3170d3140efe124cddb77722db36467@o4507025015767040.ingest.us.sentry.io/4507025115119616",
                tracePropagationTargets: ["localhost", "assure.powershield.com", "cloud.powershield.com", "assure.sandbox.powershield.com"],
                integrations: [
                    Sentry.browserTracingIntegration({ router })
                ],
            });
        })
            .catch(ex => console.error('Failed to load Sentry module:', ex));
    }
    ;
    window.isTouchDevice = ("ontouchstart" in window) || (navigator.maxTouchPoints > 0);
    ErrorHandler.register(useSentry);
    app.config.errorHandler = (err, instance, info) => {
        console.error('Vue error:', err);
    };
    app.mount('#app');
}))();
